import { trackPaymentCompleted } from 'actions';
import { Button } from 'components/ui/Button/Button';
import { SENDER_EMAIL_FIELD } from 'constants/index';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { EmbeddedRepository } from 'repositories/embedded/embedded';
import {
  getCurrentLocale,
  getFieldValue,
  getPaymentId,
  getPaymentToken,
} from 'selectors';
import { payLater as PayLater } from 'services/payLater/payLater';
import { RedirectUrl } from 'utils';
import { createMarkup } from 'utils/createMarkup/createMarkup';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { useTranslations } from 'utils/translations/useTranslations';
import './OfflineRedirection.scss';

const DEFAULT_PAYMENT_STATUS = 'initiated';

function redirectToExternalUrl(url) {
  const onFinish = EmbeddedRepository.getProp('fwFinish');
  onFinish(url);
}

const OfflineRedirectionComponent = ({
  email,
  id,
  locale,
  onClose,
  returnUrl,
  token,
  trackPaymentCompleted,
}) => {
  const i18n = useTranslations();
  // biome-ignore lint/correctness/useExhaustiveDependencies: we want it to run only when the component is mounted
  useEffect(() => {
    async function sendInstructions() {
      try {
        await PayLater.sendInstructions(id, token, email, locale);
      } catch (e) {
        await errorNotifier.notify(e);
      }
    }
    sendInstructions();
  }, []);

  const handleOnClick = async () => {
    await trackPaymentCompleted()
      .then(() => {
        if (returnUrl) {
          const returnUrlWithId = RedirectUrl.build(
            returnUrl,
            id,
            DEFAULT_PAYMENT_STATUS,
          );
          redirectToExternalUrl(returnUrlWithId.toString());
        } else {
          onClose();
        }
      })
      .catch(e => {
        errorNotifier.notify(e);
      });
  };

  return (
    <>
      <Button
        className="marginTop-xl Offline-Redirect-Button"
        labelClassName="normal-case leading-4"
        tone="primary"
        size="full"
        data-testid="closeCheckout"
        onClick={handleOnClick}
        dangerouslySetInnerHTML={createMarkup(
          i18n.t('checkout.instructions.offline.closeCheckout'),
        )}
      />
    </>
  );
};

const mapStateToProps = state => ({
  email: getFieldValue(state, SENDER_EMAIL_FIELD),
  id: getPaymentId(state),
  locale: getCurrentLocale(state),
  returnUrl: getFieldValue(state, 'return_url'),
  token: getPaymentToken(state),
});

const mapDispatchToProps = {
  trackPaymentCompleted,
};

OfflineRedirectionComponent.propTypes = {
  email: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  returnUrl: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  trackPaymentCompleted: PropTypes.func,
};

const OfflineRedirection = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OfflineRedirectionComponent);

export { OfflineRedirection, OfflineRedirectionComponent };
